// app/root.tsx
import {
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  isRouteErrorResponse,
  useRouteError,
} from "@remix-run/react";
import type { ActionFunctionArgs, LinksFunction } from "@remix-run/node";
import "./tailwind.css";
import NotFoundPage from "./components/not-found/NotFoundPage";
import { useEffect } from "react";
import { handleGlobalForm } from "./actions/handle-global-form";
import React from "react";

export const links: LinksFunction = () => [
  {
    rel: "stylesheet",
    href: "https://fonts.googleapis.com/css2?family=Inter:wght@100..900&display=swap",
  },
  {
    rel: "preload",
    as: "image",
    href: "https://ik.imagekit.io/didigi/didigi/JASA-RENOVASI-bangunrumahkita/jasa-bangun-rumah-di-bekasi_hero.webp?tr=f-avif,w-800,q-60,fo-auto",
    type: "image/avif",
    imageSrcSet: `
      https://ik.imagekit.io/didigi/didigi/JASA-RENOVASI-bangunrumahkita/jasa-bangun-rumah-di-bekasi_hero-kecil.webp?tr=f-avif,w-400,q-60,fo-auto 400w,
            https://ik.imagekit.io/didigi/didigi/JASA-RENOVASI-bangunrumahkita/jasa-bangun-rumah-di-bekasi_hero-kecil.webp?tr=f-avif,w-800,q-60,fo-auto 800w,
            https://ik.imagekit.io/didigi/didigi/JASA-RENOVASI-bangunrumahkita/jasa-bangun-rumah-di-bekasi_hero.webp?tr=f-avif,w-1200,q-60,fo-auto 1200w
    `,
    imageSizes: "(max-width: 600px) 400px, (max-width: 1024px) 800px, 800px",
  },
];

export const action = async ({ request }: ActionFunctionArgs) => {
  return handleGlobalForm(request);
};

export function Layout({ children }: { children: React.ReactNode }) {
  useEffect(() => {
    if (typeof window === "undefined") return;
    if ((window as any).__thirdPartyDeferred) return;
    (window as any).__thirdPartyDeferred = true;

    function loadScript(src: string, attrs?: Record<string, string>) {
      const s = document.createElement("script");
      s.src = src;
      s.defer = true;
      if (attrs)
        Object.entries(attrs).forEach(([k, v]) => s.setAttribute(k, v));
      document.head.appendChild(s);
      return s;
    }

    function loadFacebookPixel() {
      if ((window as any).fbq) return;
      const fbq: any = function (...args: any[]) {
        (fbq as any).callMethod
          ? (fbq as any).callMethod.apply(fbq, args)
          : (fbq as any).queue.push(args);
      };
      fbq.push = fbq;
      fbq.loaded = true;
      fbq.version = "2.0";
      fbq.queue = [];
      (window as any)._fbq = fbq;
      (window as any).fbq = fbq;

      const s = document.createElement("script");
      s.defer = true;
      s.src = "https://connect.facebook.net/en_US/fbevents.js";
      document.head.appendChild(s);
    }

    const onLoad = () => {
      // ---- Google Ads (gtag) ----
      const g = loadScript(
        "https://www.googletagmanager.com/gtag/js?id=AW-11500673088"
      );
      g.addEventListener("load", () => {
        (window as any).dataLayer = (window as any).dataLayer || [];
        function gtag(...args: any[]) {
          (window as any).dataLayer.push(args);
        }
        gtag("js", new Date());
        gtag("config", "AW-11500673088");
      });

      // ---- Ahrefs ----
      loadScript("https://analytics.ahrefs.com/analytics.js", {
        "data-key": "XIDNsEU2cJf/ZcgcMMuo4Q",
      });

      loadFacebookPixel();
      try {
        (window as any).fbq("init", "1124285182836661");
        (window as any).fbq("track", "PageView");
      } catch {}
    };

    if (document.readyState === "complete") onLoad();
    else window.addEventListener("load", onLoad);
    return () => window.removeEventListener("load", onLoad);
  }, []);

  return (
    <html lang="en">
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />

        <Meta />
        <Links />
        {/* <meta
          name="p:domain_verify"
          content="f2110ef2a83f2a5aaa6102bc73f36d37"
        /> */}
        <link rel="dns-prefetch" href="https://ik.imagekit.io" />
        <link
          rel="preconnect"
          href="https://ik.imagekit.io"
          crossOrigin="anonymous"
        />
        <link rel="dns-prefetch" href="https://fonts.googleapis.com" />
        <link
          rel="preconnect"
          href="https://fonts.googleapis.com"
          crossOrigin="anonymous"
        />
        <link rel="dns-prefetch" href="https://fonts.gstatic.com" />
        <link
          rel="preconnect"
          href="https://fonts.gstatic.com"
          crossOrigin="anonymous"
        />
      </head>
      <body>
        {children}
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  );
}

export default function App() {
  return <Outlet />;
}

// 🔴 CATCH BOUNDARY FOR 404
export function CatchBoundary() {
  return <NotFoundPage />;
}

// 🔴 ERROR BOUNDARY FOR OTHER ERRORS
export function ErrorBoundary() {
  const error = useRouteError();
  if (isRouteErrorResponse(error) && error.status === 404) {
    return <NotFoundPage />;
  }
  return (
    <div className="min-h-[100vh] flex flex-col justify-center items-center">
      <h1 className="text-2xl font-bold text-red-500 mb-4">
        Terjadi kesalahan
      </h1>
      <p className="text-gray-700">
        {error instanceof Error ? error.message : "Unknown error occurred."}
      </p>
      <a href="/" className="block mt-4 text-blue-600 hover:underline">
        Kembali ke Beranda
      </a>
    </div>
  );
}
